/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { BaseButton, isBlank } from "@cd2/components"
import { Button, IconButton, Spacer } from "@cd2/components/build/es6"
import { Grid } from "@cd2/components/build/es6/Grid"
import { Fields, Form2 } from "@cd2/forms"
import { FieldLabel, useField, useForm } from "@cd2/forms/build/es6/Form2"
import { FormState } from "@cd2/forms/build/es6/Form2/FormState"
import { H4, P } from "@cd2/text/build/es6"
import { genId } from "@cd2/tools"
import React, { useEffect, useRef, useState } from "react"
import { brandColors, themeColors } from "../../Components/EOSThemeProvider"
import { FieldGroup } from "../../Components/FieldGroup"
import { DpdProductSelectList } from "../../Components/FormFields/DpdProductSelectList"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import { CountrySelect } from "../../Components/SelectLists/CountrySelect"
import { autoCompleteOff } from "../../constants/autoComplete"
import { icons } from "../../constants/icons"
import { useApi } from "../../hooks/useApi"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { ProductSelect } from "../../stories/quoteOrders/ItemRowSageProduct"
import { IValidationResponse } from "../../types/common"
import { IRecord } from "../../types/schema"
import { spliceArray } from "../../utils/arrays"
import { formStateSetErrors } from "../../utils/formStateSetErrors"

import { assert } from "@cd2/tools/build/es6"
import { useHistory } from "react-router-dom"
import { DeliveryMethodSelectList } from "../../Components/FormFields/DeliveryMethodSelectList"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { AddressCard } from "../../Components/Misc/AddressCard"
import { OpportunityCertificatePreview } from "../../Components/OpportunityCertificatePreview"
import {
  IPostcodeLookupReturnValue,
  PostcodeField,
} from "../../Components/Pages/CustomerShowPage/PostcodeField"
import { QuickToolTip } from "../../Components/QuickTooltip"
import { prefixAddress } from "../../Components/useOrderRequestGenerator"
import { buttonConfigs } from "../../constants/buttonConfigs"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"
import { useConfirmation } from "../../hooks/useConfirmation"
import {
  ConfirmAlternateProductModal,
  useProductAlternative,
} from "../../hooks/useProductAlternative"
import { Form2ValidatePostcodeLength } from "../../utils/Form2ValidatePostcodeLength"
import { currencyNameToSymbol } from "../../utils/currency"
import { IBranchSettings, useSettings } from "../../utils/useSettings"
import { isInternalOrder } from "../../utils/utils"
import { InfoBanner } from "../AlertBanners"
import { fetchOrderRequestBranchAssoc } from "./fetchOrderRequestBranchAssoc"
import { egsBranchId, fortusEwBranchId } from "../../../shared/constants"
import { itemTypes } from "../../stories/quoteOrders/quoteOrderUtils"
import { StockAvailableInOtherBranchesPanel } from "../../Components/PurchaseOrdersForm2"
import { ProductLocationStockDisplaySwitcher } from "../../apiHooks/productLocations"

const branchAttrs = ["name"]

interface IBranch {
  id: string
  name: string
}

export interface IOrderRequest {
  dpdProduct?: IRecord<"products", "body" | "sale_price_pennies">
  created_by_branch?: { id: string; name: string }
  received_by_branch: { id: string; name: string; settings: IBranchSettings }
  ship_to_branch?: boolean
  part_ship?: boolean
  notes?: string
  items?: IItem[]
  shipping_address_name?: string
  shipping_address_line_1?: string
  shipping_address_line_2?: string
  shipping_address_line_3?: string
  shipping_address_line_4?: string
  shipping_address_city?: string
  shipping_address_county?: string
  shipping_address_country?: string
  shipping_address_postcode?: string
  shipping_address_phone?: string
  shipping_address_email?: string
  billing_address_name?: string
  billing_address_line_1?: string
  billing_address_line_2?: string
  billing_address_line_3?: string
  billing_address_line_4?: string
  billing_address_city?: string
  billing_address_county?: string
  billing_address_country?: string
  billing_address_postcode?: string
  billing_address_phone?: string
  billing_address_email?: string
  order_requested_date?: Date
  sourceOrder?: IRecord<"orders", "will_collect">
  useDirectDelivery?: boolean
  delivery_method?: string
  direct_from_supplier?: boolean
  useDeliveryMethod?: boolean
  hide_tax_on_documents?: boolean
  shunt?: boolean
  carriage_pennies?: number
}

interface IProps {
  initialData: IOrderRequest
  onSubmit: (data: IOrderRequest) => any
}

export const ORDER_REQUEST_ITEM_NEW_ID_PREFIX = "new"

export function OrderRequestForm({ initialData, onSubmit }: IProps) {
  const { canRequestDirectDeliveryOR } = useSettings()
  const { currentUser } = useGlobalState()
  const [lockBranchSelect, setLockBranchSelect] = useState(!!initialData.received_by_branch?.id)
  const alerts = useSiteAlerts()

  const [receivingBranchSettings, setReceivingBranchSettings] = useState<IBranchSettings>(
    initialData.received_by_branch.settings,
  )

  const handleSubmit = useAsyncCallback(
    async (data: IOrderRequest, formState: FormState<IOrderRequest>) => {
      let { isValid, errors } = validateForm(formState.getData())

      if (!data.ship_to_branch && receivingBranchSettings.useAddressLengthValidation) {
        const maxLength = 35
        const fields = [
          "shipping_address_city",
          "shipping_address_line_4",
          "shipping_address_line_3",
          "shipping_address_line_2",
          "shipping_address_name",
        ] as const
        fields.forEach(field => {
          if ((data[field] ?? "").length > maxLength) {
            errors.shipping_address = `${field.replace(
              /_/g,
              " ",
            )} must be at most ${maxLength} characters`
            isValid = false
          }
        })

        const line1MaxLength = 34
        if ((data.shipping_address_line_1 ?? "").length > line1MaxLength) {
          errors.shipping_address = `shipping address line 1 must be at most ${line1MaxLength} characters`
          isValid = false
        }
      }

      if (isValid) {
        formStateSetErrors(formState, {})
        await onSubmit(data)
      } else {
        if (errors.created_by_branch) {
          alerts.genericError("Please select a source branch")
        } else if (errors.received_by_branch) {
          alerts.genericError("Please select a target branch")
        } else if (errors.items_length) {
          alerts.genericError("Please check errors in your items")
        } else {
          alerts.genericError("Please check the form for errors")
        }
        console.log("FORM ERRORS", errors, "state", formState.getData())
        formStateSetErrors(formState, errors)
      }
    },
  )

  return (
    <div>
      <Form2.Form initialData={initialData} onSubmit={handleSubmit}>
        <LoadDataFromHistoryState />
        <Grid columns={[{ fr: 1 }, { fr: 1 }]}>
          <FloatingBox
            header={<H4>1. Order request</H4>}
            css={css`
              max-width: 500px;
            `}
          >
            <Grid gap={8}>
              {initialData.created_by_branch && (
                <Form2.WithField<IRecord<"branches", "name">>
                  field="received_by_branch"
                  render={branchField => {
                    function setTargetBranch(
                      branch: IRecord<"branches", "name"> & {
                        settings: IBranchSettings
                      },
                    ) {
                      branchField.setValue(branch)

                      setLockBranchSelect(true)
                      setReceivingBranchSettings(branch.settings)
                    }

                    return (
                      <div>
                        <Grid
                          columns={[{ fr: 1 }, "max-content"]}
                          gap={8}
                          alignItems="end"
                          css={css`
                            position: relative;
                            z-index: 11; /* place above product select */
                          `}
                        >
                          <OrderRequestBranchSelect
                            label="Recipient Branch"
                            branchId={initialData.created_by_branch!.id}
                            onChange={setTargetBranch}
                            branchValue={branchField.getValue()?.id}
                            disabled={lockBranchSelect}
                          />

                          <IconButton
                            icon={lockBranchSelect ? icons.lock : icons.lockOpen}
                            onClick={() => setLockBranchSelect(k => !k)}
                            buttonStyle={lockBranchSelect ? "solid" : "outline"}
                            disabled={!branchField.getValue()}
                            square
                          />
                        </Grid>
                      </div>
                    )
                  }}
                />
              )}
              <Form2.TextArea
                field="notes"
                placeholder="Leave any notes for the receiving branch..."
              />
              {canRequestDirectDeliveryOR && (
                <Form2.Checkbox
                  field="useDirectDelivery"
                  label="Products should be sent to the direct deliveries warehouse"
                />
              )}
            </Grid>
          </FloatingBox>
          <Form2.WithField<{ id: string }>
            field="sourceOrder"
            render={field => (
              <OpportunityCertificatePreview recordId={field.getValue()?.id} resource="orders" />
            )}
          />
        </Grid>
        <Form2.WithForm
          render={form => {
            const targetBranch = form.getField("received_by_branch").getValue()
            const sourceBranch = form.getField("created_by_branch").getValue()
            const shipToBranchField = form.getField("ship_to_branch")
            const sourceOrderField = form.getField("sourceOrder")
            const partShipField = form.getField("part_ship")
            if (!targetBranch || !sourceBranch) {
              return null
            }
            const isInternalFn = () => isInternalOrder(targetBranch.id, sourceBranch.id)
            const isTargetBarscan = async () => {
              if (!targetBranch) {
                return false
              }
              const branch = await window.skeem.fetch2<
                IRecord<"branches"> & { settings: IBranchSettings }
              >({
                first: {
                  name: "branches",
                  attributes: ["settings", "name"],
                  matches: { id: targetBranch.id },
                },
              })
              const isBarScan = !!branch.settings.barScanOrders

              form.getField("useDeliveryMethod").setValue(!isBarScan)
              return isBarScan
            }
            return (
              <React.Fragment>
                <OrderRequestItemsForm
                  targetBranch={targetBranch}
                  sourceBranch={sourceBranch}
                />
                <FloatingBox header={<H4>3. Addresses</H4>}>
                  <Grid gap={16} columns={[{ fr: 1 }, { fr: 1 }]}>
                    <FieldGroup
                      name="Shipping Address"
                      css={css`
                        position: relative;
                      `}
                      headerZIndex={3}
                    >
                      <ConditionalAsyncRender
                        deps={[targetBranch.id]}
                        fn={isInternalFn}
                      >
                        {isInternal => (
                          <React.Fragment>
                            <InternalPriceUpdater shouldUpdate={isInternal} />
                            {sourceOrderField.getValue()?.will_collect && (
                              <div
                                css={css`
                                  width: 100%;
                                  height: 100%;
                                  background-color: ${brandColors.midGrey};
                                  opacity: 0.8;
                                  z-index: 2;
                                  position: absolute;
                                  display: flex;
                                  flex-direction: column;
                                  justify-content: center;
                                  align-items: center;
                                  top: 0;
                                  left: 0;
                                  border-radius: 6px;
                                  padding: var(--floating-box-child-padding);
                                `}
                              >
                                <P size="large">Customer will collect from branch</P>
                              </div>
                            )}
                            <div
                              css={css`
                                z-index: 1;
                              `}
                            >
                              <Form2.WithField
                                field="shipping_address"
                                render={field =>
                                  field.getError() ? (
                                    <div
                                      css={css`
                                        background: ${brandColors.red};
                                        color: white;
                                        padding: 4px 8px;
                                        border-radius: 4px;
                                        margin-bottom: 8px;
                                      `}
                                    >
                                      <b>
                                        Please update the source order before making an order
                                        request.
                                      </b>
                                      <Spacer size={4} />
                                      {field.getError()}
                                    </div>
                                  ) : null
                                }
                                subscribe
                              />
                              <React.Fragment>
                                <Fields.Checkbox
                                  label="Ship this direct to branch"
                                  onChange={value => {
                                    shipToBranchField.setValue(value)
                                    partShipField.setValue(value)
                                  }}
                                  value={shipToBranchField.getValue()}
                                  disabled={
                                    sourceOrderField.getValue()?.address?.line_1 === "" ||
                                    sourceOrderField.getValue()?.will_collect
                                  }
                                />
                                {sourceOrderField.getValue()?.address?.line_1 === "" && (
                                  <React.Fragment>
                                    <Spacer size={4} />
                                    <P size="small">
                                      This order request is being generated from an order which does
                                      not have a delivery address.<br></br>Add a delivery address to
                                      ship to customer.
                                    </P>
                                  </React.Fragment>
                                )}

                                <Spacer />
                                <InfoBanner
                                  message={
                                    shipToBranchField.getValue()
                                      ? isInternal
                                        ? "This order request will be processed via an internal transfer"
                                        : "This order request will be shipped to your branch"
                                      : "This order request will be shipped direct to customer"
                                  }
                                />
                                <Spacer />
                                {!shipToBranchField.getValue() && (
                                  <React.Fragment>
                                    <SourceOrderSelect />
                                    <Spacer />
                                  </React.Fragment>
                                )}

                                <AddressLoader
                                  type="shipping"
                                  sourceBranchId={sourceBranch.id}
                                  receivingBranchId={targetBranch.id}
                                  isInternal={isInternal}
                                />
                              </React.Fragment>
                            </div>
                            {sourceOrderField.getValue()?.will_collect && (
                              <div
                                css={css`
                                  position: relative;
                                  z-index: 3;
                                `}
                              >
                                <SourceOrderSelect />
                              </div>
                            )}
                          </React.Fragment>
                        )}
                      </ConditionalAsyncRender>
                    </FieldGroup>

                    <FieldGroup name="Billing Address">
                      <ConditionalAsyncRender
                        deps={[targetBranch.id]}
                        fn={isInternalFn}
                      >
                        {isInternal => (
                          <AddressLoader
                            type="billing"
                            sourceBranchId={sourceBranch.id}
                            receivingBranchId={targetBranch.id}
                            isInternal={isInternal}
                          />
                        )}
                      </ConditionalAsyncRender>
                    </FieldGroup>
                  </Grid>
                </FloatingBox>
                <FloatingBox header={<H4>4. Delivery</H4>}>
                  <ConditionalAsyncRender
                    deps={[targetBranch.id]}
                    fn={isTargetBarscan}
                  >
                    {isBarscan =>
                      isBarscan ? (
                        <Form2.WithField<IOrderRequest["dpdProduct"]>
                          field="dpdProduct"
                          render={field => (
                            <DpdProductSelectList
                              label={
                                <FieldLabel
                                  label={<span>Delivery Service</span>}
                                  fieldName={`dpdProduct`}
                                  required
                                />
                              }
                              filter={{ not: { matches: { body: { like: "direct del" } } } }}
                              branchId={targetBranch.id}
                              onChange={dpdProduct => {
                                field.setValue(dpdProduct || undefined)
                              }}
                              value={field.getValue()}
                              error={field.getError()}
                              orderRequestFilter
                            />
                          )}
                        />
                      ) : (
                        <React.Fragment>
                          <Form2.WithField<string>
                            field="delivery_method"
                            render={field => (
                              <DeliveryMethodSelectList
                                onChange={field.setValue}
                                value={field.getValue()}
                                branchId={targetBranch.id}
                                error={field.getError()}
                                requireBranchId
                                required
                              />
                            )}
                          />
                        </React.Fragment>
                      )
                    }
                  </ConditionalAsyncRender>
                  <Spacer />
                  <Form2.Date field="order_requested_date" label="Order Requested Date" />
                  <Spacer />
                  <Form2.Checkbox field="part_ship" label="Allow partial shipment" />
                  <Spacer />
                  <ConditionalAsyncRender
                    deps={[targetBranch.id, initialData.created_by_branch?.id]}
                    fn={async () => {
                      // CHECK if self is external and target is hub - show direct from supplier if true
                      const branch = await window.skeem.fetch2<
                        IRecord<"branches", "is_fortus_ew_branch">
                      >({
                        first: {
                          name: "branches",
                          attributes: ["is_fortus_ew_branch"],
                          filter: { matches: { id: initialData.created_by_branch?.id } },
                        },
                      })

                      if (branch && targetBranch.id === fortusEwBranchId) {
                        return true
                      }
                      // force override this to false when it shouldn't be shown
                      form.getField("direct_from_supplier").setValue(false)
                      return false
                    }}
                  >
                    {showDirectFromSupplier => {
                      if (!showDirectFromSupplier) {
                        return null
                      }
                      return <Form2.Checkbox field="direct_from_supplier" />
                    }}
                  </ConditionalAsyncRender>
                  <Spacer />
                  <Form2.WithField<number | null>
                    field="carriage_pennies"
                    render={field => (
                      <Fields.Price
                        value={field.getValue()}
                        onChange={field.setValue}
                        label="Carriage"
                        inPennies
                      />
                    )}
                  />
                </FloatingBox>
                <Form2.Checkbox
                  field="hide_tax_on_documents"
                  label="Hide VAT on Purchase Order PDF"
                />
                {currentUser.canShuntOrderRequests && (
                  <Form2.Checkbox field="shunt" label="Mark this order request as SHUNT" />
                )}
              </React.Fragment>
            )
          }}
          subscribe={{ dataChange: true }}
        />
        <div>
          <BaseButton {...buttonConfigs.formSaveButton} disabled={handleSubmit.running} />
        </div>
      </Form2.Form>
    </div>
  )
}

const addressAttrs = [
  "name",
  "line_1",
  "line_2",
  "line_3",
  "line_4",
  "city",
  "county",
  "country",
  "postcode",
  "phone",
  "email",
] as const
type IAddressForSourceOrder = IRecord<"addresses", (typeof addressAttrs)[number]>
type ISourceOrderAddress = IRecord<"orders"> & {
  billing_address: IAddressForSourceOrder
  address: IAddressForSourceOrder
}

function AddressLoader({
  type,
  sourceBranchId,
  receivingBranchId,
  isInternal,
}: {
  type: "shipping" | "billing"
  sourceBranchId: string
  receivingBranchId: string
  isInternal: boolean
}) {
  const form = useForm()
  const sourceOrderField = useField<{ id: string } | undefined>("sourceOrder", { subscribe: true })
  const shipToField = useField("ship_to_branch", { subscribe: true })

  const [sourceOrder, setSourceOrder] = useState<ISourceOrderAddress | null>(null)
  const [branchBillingAddress, setBranchBillingAddress] = useState<IAddressForSourceOrder | null>(
    null,
  )
  const [branchShippingAddress, setBranchShippingAddress] =
    useState<IAddressForSourceOrder | null>()

  useAsyncEffect(async () => {
    const sourceOrderId = sourceOrderField.getValue()?.id
    if (sourceOrderId) {
      const sourceOrder = await window.skeem.fetch2<ISourceOrderAddress>({
        first: {
          name: "orders",
          attributes: [
            { name: "address", attributes: addressAttrs },
            { name: "billing_address", attributes: addressAttrs },
          ],
          matches: { id: sourceOrderId },
        },
      })
      setSourceOrder(sourceOrder)
    } else {
      setSourceOrder(null)
    }
  }, [sourceOrderField.getValue()?.id])

  useAsyncEffect(async () => {
    // Find the order request association that links the source and target branches
    const orderRequestAssoc = await window.skeem.fetch2<
      IRecord<"orderRequestBranches"> & {
        billingAddress: IAddressForSourceOrder
        shippingAddress: IAddressForSourceOrder
      }
    >({
      first: {
        name: "orderRequestBranches",
        attributes: [
          { name: "billingAddress", attributes: addressAttrs },
          {
            name: "shippingAddress",
            attributes: addressAttrs,
          },
        ],
        filter: {
          matches: {
            sourceBranch: sourceBranchId,
            targetBranch: receivingBranchId,
          },
        },
      },
    })
    if (orderRequestAssoc) {
      setBranchBillingAddress(orderRequestAssoc.billingAddress)
      setBranchShippingAddress(orderRequestAssoc.shippingAddress)
    }
  }, [sourceBranchId, receivingBranchId])

  const branchAddress = type === "billing" ? branchBillingAddress : branchShippingAddress
  const sourceOrderAddress =
    type === "billing" ? sourceOrder?.billing_address ?? sourceOrder?.address : sourceOrder?.address

  const address =
    (!isInternal && type === "billing") || shipToField.getValue()
      ? branchAddress
      : sourceOrderAddress

  useEffect(() => {
    const updateAddr = prefixAddress(type, address!)
    form.setPartialData({ ...updateAddr })
  }, [JSON.stringify(address)])

  const fieldErrors = [
    { field: `${type}_address_city`, friendly: "City" },
    { field: `${type}_address_country`, friendly: "Country" },
    { field: `${type}_address_county`, friendly: "County" },
    { field: `${type}_address_line_1`, friendly: "Line 1" },
    { field: `${type}_address_line_2`, friendly: "Line 2" },
    { field: `${type}_address_line_3`, friendly: "Line 3" },
    { field: `${type}_address_name`, friendly: "Name" },
    { field: `${type}_address_postcode`, friendly: "Postcode" },
  ].reduce((acc, field) => {
    const error = form.getField(field.field).getError()
    if (error) {
      acc[field.field] = `${field.friendly} ${error}`
    }
    return acc
  }, {} as Record<string, string>)

  const hasError = Object.keys(fieldErrors).length > 0

  if (!sourceOrder && !shipToField.getValue()) {
    return <P>Select a source order</P>
  }

  if (!address) {
    return <P>Loading...</P>
  }

  return (
    <div>
      <AddressCard address={address} hasError={hasError} />
      {hasError && (
        <P color={brandColors.blaze} size="small">
          {Object.values(fieldErrors).join(", ")}
        </P>
      )}
    </div>
  )
}

type ISourceOrder = IRecord<"orders", "identifier" | "will_collect"> & {
  customer: IRecord<"customers", "name">
}

export function SourceOrderSelect() {
  const skeem = window.skeem
  const { currentUser } = useGlobalState()
  const sourceOrderField = Form2.useField<{ will_collect?: boolean } | undefined>("sourceOrder", {
    subscribe: true,
  })
  const shipToBranchField = Form2.useField("ship_to_branch", { subscribe: true })
  useEffect(() => {
    sourceOrderField.events.on(Form2.fieldEvents.change, () => {
      const order = sourceOrderField.getValue()
      if (order && order.will_collect) {
        shipToBranchField.setValue(true)
      }
    })
  }, [])

  function handleLoadOrders(query: string) {
    return skeem.fetch2<ISourceOrder[]>({
      model: {
        name: "orders",
        attributes: ["identifier", "will_collect", { name: "customer", attributes: ["name"] }],
        filter: {
          and: [
            {
              matches: {
                customer: { not: null },
                branch: currentUser.branch.id,
                archived: false,
                identifier: { like: query },
              },
            },
            {
              // must have a delivery address with line 1
              eq: [
                {
                  count: {
                    path: [{ name: "address", filter: { matches: { line_1: { not: "" } } } }],
                  },
                },
                { value: 1 },
              ],
            },
            {
              eq: [
                {
                  count: {
                    path: [
                      {
                        name: "items",
                        filter: {
                          matches: { type: { or: [itemTypes.buildSet, itemTypes.buildSetItem] } },
                        },
                      },
                    ],
                  },
                },
                { value: 0 },
              ],
            },
          ],
        },
        sort: { by: "created_at", direction: "desc" },
        paginate: { size: 30 },
      },
    })
  }

  return (
    <Form2.AsyncSelect
      field="sourceOrder"
      loadOptions={handleLoadOrders}
      getOptionLabel={opt => opt.identifier}
      getOptionValue={opt => opt.id}
      validations={{ presence: true }}
      formatOptionLabel={(opt, ctx) => {
        const option = opt
        const data = ctx as {
          context: "value" | "menu"
          inputValue: ""
          selectValue: (typeof opt)[]
        }
        const isSelectedOpen = data.selectValue[0]?.id === option.id && data.context === "menu"
        const color = isSelectedOpen ? "white" : undefined
        return (
          <div>
            <P color={color}>{opt.identifier}</P>
            {opt.customer && (
              <P color={color} size="small">
                {opt.customer.name}
              </P>
            )}
          </div>
        )
      }}
      isClearable
    />
  )
}

function InternalPriceUpdater({ shouldUpdate }: { shouldUpdate: boolean }) {
  useAutoPriceUpdater(shouldUpdate)
  return null
}

function useAutoPriceUpdater(shouldUpdate: boolean) {
  const hasMountedRef = useRef(false)
  const shouldUpdateRef = useRef(shouldUpdate)
  shouldUpdateRef.current = shouldUpdate
  const sourceOrderField = useField<{ id: string } | undefined>("sourceOrder", { subscribe: true })
  const shipToBranchField = useField("ship_to_branch", { subscribe: true })
  const itemsField = useField<IItem[]>("items", { subscribe: true })
  const { _loaded } = useSettings()
  const { currentUser } = useGlobalState()

  async function handlePriceUpdate() {
    if (!shouldUpdateRef.current) {
      console.log("[AutoPriceUpdater]: not internal")
      return
    }
    console.log("[AutoPriceUpdater]: syncing prices")
    assert(currentUser.branch?.id, "User must have a branch")

    const shipToBranch = shipToBranchField.getValue()
    const items = itemsField.getValue()
    const sourceOrder = sourceOrderField.getValue()

    if (!shipToBranch) {
      if (!sourceOrder) {
        return
      }
      const order = await window.skeem.fetch2<
        IRecord<"orders"> & {
          items: IRecord<"order_items", "product" | "price_pennies" | "discountPercent">[]
        }
      >({
        first: {
          name: "orders",
          attributes: [
            { name: "items", attributes: ["product", "price_pennies", "discountPercent"] },
          ],
          matches: { id: sourceOrder.id },
        },
      })
      const updatedItemPrices = items.map(item => {
        const matchingItem =
          item.type === "product"
            ? order.items.find(i => i.product?.id === item.source_product?.id)
            : undefined
        return item.type === "product"
          ? {
              ...item,
              price_pennies: matchingItem?.price_pennies ?? item.price_pennies,
              discountPercent: matchingItem?.discountPercent ?? null,
            }
          : item
      })
      itemsField.setValue(updatedItemPrices)
    } else {
      const updatedItemPrices = items.map(item =>
        item.type === "product"
          ? {
              ...item,
              price_pennies: item.target_cost_price_pennies || item.source_cost_price_pennies,
              discountPercent: null,
            }
          : item,
      )
      itemsField.setValue(updatedItemPrices)
    }
  }

  useEffect(() => {
    if (_loaded && !hasMountedRef.current) {
      hasMountedRef.current = true
      shipToBranchField.events.on(Form2.fieldEvents.change, handlePriceUpdate)
      sourceOrderField.events.on(Form2.fieldEvents.change, handlePriceUpdate)
    }
  }, [_loaded, shouldUpdate])

  return null
}

export interface IOrderRequestErrors {
  created_by_branch?: string
  received_by_branch?: string
  items_length?: string
  billing_address_name?: string
  billing_address_line_1?: string
  billing_address_postcode?: string
  shipping_address_name?: string
  shipping_address_line_1?: string
  shipping_address_postcode?: string
  items?: Record<string, { quantity?: string; price_pennies?: string; target_product?: string }>
  dpdProduct?: string
  delivery_method?: string
  direct_from_supplier?: string
  shipping_address?: string
}

function validateForm(data: IOrderRequest): IValidationResponse<IOrderRequestErrors> {
  let isValid = true
  const errors: IOrderRequestErrors = {}

  const productItems = data.items?.filter(item => item.type === "product")

  const textItems = data.items?.filter(item => item.type === "text") as IProductItem[] | undefined

  // require one of these fields
  if (data.useDeliveryMethod && !data.delivery_method) {
    isValid = false
    errors.delivery_method = "is required"
  } else if (!data.dpdProduct) {
    isValid = false
    errors.dpdProduct = "is required"
  }

  if (!data.created_by_branch) {
    isValid = false
    errors.created_by_branch = "is required"
  }
  if (!data.received_by_branch) {
    isValid = false
    errors.received_by_branch = "is required"
  }
  if (!productItems || productItems.length === 0) {
    isValid = false
    errors.items_length = "You must have at least 1 item"
  }

  if (productItems?.some(item => item.target_product?.product_group?.preventOrderRequest)) {
    isValid = false
    errors.items_length = "Please remove items that are not available at the target branch"
  }

  const addressTypes = ["shipping_address", "billing_address"] as const
  for (const addressType of addressTypes) {
    if (addressType === "shipping_address" && data.ship_to_branch) {
      continue
    }

    if (isBlank(data[`${addressType}_name`])) {
      isValid = false
      errors[`${addressType}_name`] = "is required"
    }
    if (isBlank(data[`${addressType}_line_1`])) {
      isValid = false
      errors[`${addressType}_line_1`] = "is required"
    }
    if (
      isBlank(data[`${addressType}_postcode`]) &&
      data[`${addressType}_country`] === "United Kingdom"
    ) {
      isValid = false
      errors[`${addressType}_postcode`] = "is required"
    }
  }

  for (const item of productItems ?? []) {
    const itemErrors: any = {}
    if (item.body) {
      continue
    }
    if (!item.quantity || item.quantity < 1) {
      isValid = false
      itemErrors.quantity = "Quantity must be greater than 0"
    }

    if (!item.price_pennies) {
      isValid = false
      itemErrors.price_pennies = "Price must be greater than 0"
    }

    if (Object.keys(itemErrors).length > 0) {
      errors.items = errors.items ?? {}
      errors.items[item.id] = itemErrors
    }
  }

  for (const item of textItems ?? []) {
    const itemErrors: any = {}
    item.body = item.body?.trim()
    if (!item.body) {
      itemErrors.body = "is required"
      isValid = false
    }
    if (Object.keys(itemErrors).length > 0) {
      errors.items = errors.items ?? {}
      errors.items[item.id] = itemErrors
    }
  }

  return { isValid, errors }
}

export type IProductItem = {
  id: string
  source_product?: {
    id: string
    stock_code: string
    body: string
    product_locations: IRecord<"product_locations">[]
  } | null
  target_product?: {
    id: string
    stock_code: string
    product_group?: { preventOrderRequest: boolean }
    product_locations: IRecord<"product_locations">[]
  } | null
  quantity?: number | null
  source_cost_price_pennies?: number | null
  target_cost_price_pennies?: number | null
  price_pennies?: number | null
  stock_code?: string
  body?: string
  buildRequired?: boolean
  type: "product"
  bypassed_stock_check?: boolean
  alternative?: boolean
  discountPercent?: number | null
  opportunity_certificate_cost_discount_amount_pennies?: number | null
  product_location?: IRecord<"product_locations">
}
export type ITextItem = {
  id: string
  body?: string
  type: "text"
  commentLine: true
}
export type IItem = IProductItem | ITextItem

interface IOrderRequestItemsFormProps {
  targetBranch: IBranch
  sourceBranch: IBranch
}

function OrderRequestItemsForm({
  targetBranch,
  sourceBranch,
}: IOrderRequestItemsFormProps) {
  const skeem = window.skeem
  const [currency, setCurrency] = useState<string>("£")

  async function setCurrencyFromAssoc() {
    const res = await fetchOrderRequestBranchAssoc(
      skeem,
      sourceBranch.id,
      targetBranch.id,
    )
    setCurrency(currencyNameToSymbol(res?.supplier?.currency_name) || "£")
  }

  useAsyncEffect(setCurrencyFromAssoc, [sourceBranch.id, targetBranch.id])
  const itemsLengthError = useField("items_length", { subscribe: true }).getError()
  const itemsField = useField<IItem[]>("items", {
    subscribe: true,
    defaultValue: [],
  })

  const [loadedSourceOrder, setLoadedSourceOrder] = useState<
    | (IRecord<"orders", "hasOpportunityCertificate"> & {
        hasAnyDiscount: boolean
      })
    | null
  >(null)
  const sourceOrderField = useField<{ id: string } | undefined>("sourceOrder", { subscribe: true })

  async function loadHasOppCert() {
    const sourceOrderId = sourceOrderField.getValue()?.id
    if (sourceOrderId) {
      const order = await window.skeem.fetch2<
        IRecord<"orders", "hasOpportunityCertificate"> & { hasAnyDiscount: boolean }
      >({
        first: {
          name: "orders",
          attributes: [
            "hasOpportunityCertificate",
            {
              as: "hasAnyDiscount",
              value: {
                gt: [
                  {
                    count: {
                      path: [
                        { name: "items", filter: { matches: { discountPercent: { not: null } } } },
                      ],
                    },
                  },
                  { value: 0 },
                ],
              },
            },
          ],
          matches: { id: sourceOrderId },
        },
      })
      setLoadedSourceOrder(order ?? null)
    } else {
      setLoadedSourceOrder(null)
    }
  }

  sourceOrderField.events.on(Form2.fieldEvents.change, loadHasOppCert)
  useAsyncEffect(loadHasOppCert, [])

  // Add new item
  function handleAddProduct() {
    itemsField.setValue([
      ...itemsField.getValue(),
      { quantity: 1, id: `${ORDER_REQUEST_ITEM_NEW_ID_PREFIX}_${genId()}`, type: "product" },
    ])
  }

  function handleAddTextItem() {
    itemsField.setValue([
      ...itemsField.getValue(),
      { id: `${ORDER_REQUEST_ITEM_NEW_ID_PREFIX}_${genId()}`, type: "text", commentLine: true },
    ])
  }

  // Remove item
  function handleRemove(index: number) {
    itemsField.setValue(itemsField.getValue().filter((_, i) => i !== index))
  }

  // Change item
  function handleChange(index: number, value: IItem) {
    itemsField.setValue(spliceArray(itemsField.getValue(), index, 1, value))
  }

  const includeDiscountColumn = !!(
    loadedSourceOrder?.hasOpportunityCertificate || loadedSourceOrder?.hasAnyDiscount
  )

  return (
    <FloatingBox header={<H4>2. Add products</H4>}>
      {itemsLengthError && (
        <P
          color="red"
          css={css`
            margin-bottom: 16px;
          `}
        >
          {itemsLengthError}
        </P>
      )}
      <Grid gap={16}>
        {itemsField.getValue().length > 0 && (
          <Grid columns={COLUMNS({ discountPct: includeDiscountColumn })} gap={8}>
            <FieldLabel label={<b>Product</b>} fieldName={`product`} required />
            <FieldLabel label={<b>PO Product Location</b>} fieldName={`product_location`} />
            <FieldLabel label={<b>Qty</b>} fieldName={`quantity`} required />
            {includeDiscountColumn && (
              <FieldLabel label={<b>Discount %</b>} fieldName={`discountPercent`} />
            )}
            <FieldLabel label={<b>Price</b>} fieldName={`price_pennies`} required />
            <FieldLabel label={<b>Build Req.</b>} fieldName={`price_pennies`} />
            <FieldLabel label={<b>Your stock</b>} fieldName={`price_pennies`} />
            <FieldLabel label={<b>Their stock</b>} fieldName={`price_pennies`} />
            <div />
          </Grid>
        )}
        {itemsField.getValue().map((item, index) => {
          if (item.type === "product") {
            return (
              <OrderRequestProductItemForm
                key={item.id}
                onChange={item => handleChange(index, item)}
                targetBranch={targetBranch}
                sourceBranch={sourceBranch}
                item={item}
                error={itemsField.getError()?.[item.id]}
                onRemove={() => handleRemove(index)}
                includeDiscountColumn={includeDiscountColumn}
                currency={currency}
              />
            )
          } else if (item.type === "text") {
            return (
              <OrderRequestTextItemForm
                key={item.id}
                onChange={item => handleChange(index, item)}
                item={item}
                error={itemsField.getError()?.[item.id]}
                onRemove={() => handleRemove(index)}
              />
            )
          }
        })}
      </Grid>
      <div
        css={css`
          text-align: right;
          margin-left: -16px;
          margin-right: -16px;
          padding-left: 16px;
          padding-right: 16px;

          ${itemsField.getValue().length > 0 &&
          css`
            margin-top: 16px;
            padding-top: 16px;
            border-top: 1px solid var(--floating-box-border-color);
          `}
        `}
      >
        <Button
          leftIcon={icons.plus}
          rightIcon={icons.products}
          onClick={handleAddProduct}
          buttonColor="white"
        >
          Add product
        </Button>
        <Spacer horizontal />
        <Button
          leftIcon={icons.plus}
          rightIcon={icons.text}
          onClick={handleAddTextItem}
          buttonColor="white"
        >
          Add text line
        </Button>
      </div>
    </FloatingBox>
  )
}

interface IProduct {
  id: string
  stock_code: string
  body: string
  branch: IBranch
  cost_price_pennies: number
  preferred_supplier_list_price_pennies: number
  product_group: { preventOrderRequest: boolean }
  product_locations: { id: string; warehouse?: { id: string } }[]
  product_master: { id: string } | null
}

function OrderRequestProductItemForm({
  targetBranch,
  sourceBranch,
  item,
  onChange,
  error,
  onRemove,
  includeDiscountColumn,
  currency,
}: {
  sourceBranch: IBranch
  targetBranch: IBranch
  item: IProductItem
  onChange(item: IProductItem): void
  error?: Exclude<IOrderRequestErrors["items"], undefined>[0]
  onRemove(): void
  includeDiscountColumn?: boolean
  currency: string
}) {
  const sourceOrderField = useField<{ id: string } | undefined>("sourceOrder", { subscribe: true })
  const shipToBranchField = useField("ship_to_branch", { subscribe: true })
  const hasMountedRef = useRef(false)
  const [confirmAltProductNode, confirmAltProduct] = useConfirmation<any, { id: string }>(
    ({ onResolve, data }) => <ConfirmAlternateProductModal data={data} onResolve={onResolve} />,
  )
  const checkProductForAlternative = useProductAlternative()

  // Look up product to verify exists in both branches
  async function handleLoadProductData(selectedProduct: { id: string }, skipAltCheck?: boolean) {
    const isInternal = await isInternalOrder(sourceBranch.id, targetBranch.id)
    let ogProduct = selectedProduct
    const alt = await checkProductForAlternative(ogProduct.id)
    if (alt.alternative && !skipAltCheck) {
      const selected = await confirmAltProduct(alt)
      const loaded = await window.skeem.fetch2<IRecord<"products">>({
        first: {
          name: "products",

          filter: { matches: { id: selected.id } },
        },
      })
      ogProduct = loaded
    }

    const { product, sourceBranchData, targetBranchData, alternative, targetBranchPrice } =
      await handleLoadOrderRequestProductData({
        sourceBranchId: sourceBranch.id,
        targetBranchId: targetBranch.id,
        productId: ogProduct.id,
      })

    // IF non internal - should use load the target branch price always
    let targetPrice = null
    const sourceOrder = sourceOrderField.getValue()
    const shipToBranch = shipToBranchField.getValue()
    // IF internal
    if (isInternal) {
      // Use the source branch price if not ship to branch
      if (!shipToBranch) {
        if (sourceOrder) {
          const order = await window.skeem.fetch2<
            IRecord<"orders"> & {
              items: IRecord<"order_items", "product" | "price_pennies">[]
            }
          >({
            first: {
              name: "orders",
              attributes: [
                {
                  name: "items",
                  attributes: ["product", "price_pennies"],
                },
              ],
              matches: { id: sourceOrder.id },
            },
          })

          const matchingItem =
            item.type === "product"
              ? order.items.find(i => i.product?.id === item.source_product?.id)
              : undefined
          targetPrice = matchingItem?.price_pennies ?? item.price_pennies
        }
      } else {
        // otherwise use target branch price
        targetPrice = targetBranchPrice
      }
    } else {
      targetPrice = targetBranchPrice
    }

    onChange({
      ...item,
      stock_code: product.stock_code,
      body: product.body,
      target_cost_price_pennies: targetBranchData?.cost_price_pennies,
      source_cost_price_pennies: sourceBranchData?.cost_price_pennies,
      target_product: targetBranchData,
      source_product: sourceBranchData,
      price_pennies: targetPrice,
      alternative,
      type: "product",
    })
  }

  useAsyncEffect(async () => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }
    if (item.source_product && item.stock_code && item.body) {
      await handleLoadProductData({ id: item.source_product.id }, true)
    }
  }, [targetBranch.id, item.stock_code])

  return (
    <div
      css={css`
        padding: 2px 8px;
        margin: 0 -8px;
        transition: 0.2s;
      `}
    >
      {confirmAltProductNode}
      <div
        css={
          item.target_product?.product_group?.preventOrderRequest
            ? css`
                background-color: ${themeColors.lightRed};
              `
            : undefined
        }
      >
        <Grid columns={COLUMNS({ discountPct: includeDiscountColumn })} gap={8}>
          <div
            css={css`
              position: relative;
              z-index: 10;
            `}
          >
            <ProductSelect
              value={item.source_product?.id}
              onChange={handleLoadProductData}
              error={error?.target_product}
              preventSelectUnexported
            />
            {item.alternative && (
              <React.Fragment>
                <Spacer size={8} />
                <QuickToolTip
                  content={
                    <P color="white">
                      This product has a flagged alternative item. When this order request is saved,
                      it will automatically be swapped for {item.target_product?.stock_code}
                    </P>
                  }
                >
                  <span
                    css={css`
                      color: grey;
                      border-bottom: 1px dotted grey;
                    `}
                  >
                    This item will be swapped for an alternative when requested
                  </span>
                </QuickToolTip>
              </React.Fragment>
            )}
            <BoxQuantitySwitch
              itemQuantity={item.quantity}
              onChange={qty => onChange({ ...item, quantity: qty })}
              productId={item.source_product?.id}
            />
          </div>
          <div>
            <ProductLocationSelect
              productId={item.source_product?.id}
              value={item.product_location?.id}
              onChange={value => onChange({ ...item, product_location: { id: value } })}
            />
          </div>
          <Fields.Number2
            value={item.quantity}
            onChange={value => onChange({ ...item, quantity: value })}
            placeholder="Stock 000"
          />
          {includeDiscountColumn && (
            <Fields.Number
              prefix="%"
              placeholder="10"
              value={
                item.discountPercent || item.discountPercent === 0
                  ? String(item.discountPercent)
                  : null
              }
              onChange={val => onChange({ ...item, discountPercent: Number(val) })}
              inputProps={{ min: 0, step: 0.01 }}
            />
          )}
          <Fields.Price
            value={item.price_pennies ?? null}
            onChange={value => onChange({ ...item, price_pennies: value })}
            error={error?.price_pennies}
            prefix={currency}
            inPennies
          />
          <Fields.Checkbox
            onChange={value => onChange({ ...item, buildRequired: value })}
            value={!!item.buildRequired}
          />
          <div>
            {item.source_product && (
              <ProductLocationStockDisplaySwitcher
                productLocationIds={item.source_product.product_locations.map(l => l.id)}
              />
            )}
          </div>
          <div>
            {item.target_product && (
              <ProductLocationStockDisplaySwitcher
                productLocationIds={item.target_product.product_locations.map(l => l.id)}
              />
            )}
            {!item.target_product && item.source_product && (
              <span
                css={css`
                  font-size: 12px;
                `}
              >
                This product is not in this branch{`'`}s Sage.
                <br />
                You can still make an order request.
              </span>
            )}
          </div>
          <IconButton
            onClick={onRemove}
            icon={icons.cross}
            buttonColor={brandColors.blaze}
            square
          />
        </Grid>
        <StockAvailableInOtherBranchesPanel product={item.target_product || undefined} />
        {item.target_product?.product_group?.preventOrderRequest && (
          <div>
            <P weight={600}>This branch has restricted ordering to this product group</P>
          </div>
        )}
      </div>
    </div>
  )
}

const plAttrs = ["name", "specific_location"] as const
type IProductLocation = IRecord<"product_locations", (typeof plAttrs)[number]>

function ProductLocationSelect({
  productId,
  value,
  onChange,
}: {
  productId?: string
  value?: string
  onChange: (id: string) => void
}) {
  const [locations, setLocations] = useState<IProductLocation[]>([])
  const [selectedLocation, setSelectedLocation] = useState<IProductLocation | null>(null)

  async function loadLocations() {
    if (!productId) {
      setLocations([])
      return
    }
    const pl = await window.skeem.fetch2<IProductLocation[]>({
      model: {
        name: "product_locations",
        attributes: plAttrs,
        filter: { matches: { product: productId } },
      },
    })
    setLocations(pl)
  }

  async function loadSelected() {
    if (!value) {
      setSelectedLocation(null)
    } else if (value !== selectedLocation?.id) {
      const pl = await window.skeem.fetch2<IProductLocation>({
        first: {
          name: "product_locations",
          attributes: plAttrs,
          filter: { matches: { id: value } },
        },
      })
      setSelectedLocation(pl)
    }
  }

  useAsyncEffect(loadLocations, [productId])
  useAsyncEffect(loadSelected, [value])

  return (
    <Fields.Select2
      options={locations}
      getOptionLabel={opt => opt.name}
      getOptionValue={opt => opt.id}
      onChange={opt => {
        onChange(opt?.id)
        setSelectedLocation(opt)
      }}
      formatOptionLabel={(opt, ctx) => {
        const isMenuOpen = ctx.context === "menu"
        const isSelected = (ctx.selectValue as any as { id: string }[])?.[0]?.id === opt.id
        return (
          <div>
            <P color={isMenuOpen && isSelected ? "white" : undefined} blockSpacing={0}>
              {opt.name}
              {opt.specific_location ? (
                <span
                  css={css`
                    font-size: 12px;
                    color: #999;
                  `}
                >
                  {" "}
                  ({opt.specific_location})
                </span>
              ) : (
                ""
              )}
            </P>
          </div>
        )
      }}
      value={selectedLocation}
      postFieldText={
        <React.Fragment>
          <Spacer size={4} />
          <P blockSpacing={0} size="small" color="#7b7b7b">
            Leave blank to use default location
          </P>
        </React.Fragment>
      }
      isClearable
    />
  )
}

export function useStockedInAnotherBranch() {
  const skeem = window.skeem
  const api = useApi()
  const [loadedStockBranches, setLoadedStockBranches] = useState<
    (IRecord<"branches", "name"> & { quantity: number })[] | null
  >(null)

  async function handleLoadRelatedProductsStock({
    product,
  }: {
    product?: IRecord<"products", "product_master">
  }) {
    if (!product || !product.id || !product.product_master?.id) {
      setLoadedStockBranches(null)
      return
    }
    const relatedProducts = await window.skeem.fetch2<
      (IRecord<"products"> & { branch: IRecord<"branches"> & { settings: IBranchSettings } })[]
    >({
      model: {
        name: "products",
        attributes: [{ name: "branch", attributes: ["settings"] }],
        filter: {
          and: [
            {
              not: {
                exists: {
                  path: [
                    {
                      name: "branch",
                      filter: {
                        or: [
                          { matches: { name: { like: "test" } } },
                          { matches: { name: { like: "demo" } } },
                          { matches: { name: { like: "training" } } },
                          { matches: { name: { like: "hub" } } },
                        ].filter(Boolean),
                      },
                    },
                  ],
                },
              },
            },
            { not: { matches: { id: product.id } } },
            {
              matches: {
                product_master: product.product_master.id,
              },
            },
          ],
        },
      },
    })

    const branchesWithStock: [string, number][] = []
    const groupedProducts = relatedProducts.reduce((branch, product) => {
      const branchId = product.branch.id
      if (!branch[branchId]) {
        branch[branchId] = []
      }
      branch[branchId].push(product)
      return branch
    }, {} as Record<string, typeof relatedProducts>)

    const branchIds = Object.keys(groupedProducts)

    for (const branchId of branchIds) {
      const products = groupedProducts[branchId]
      const settings = products[0].branch.settings ?? { stockWarehouses: [] }
      const warehouses =
        (settings.stockWarehouses ?? []).length > 0 ? settings.stockWarehouses : undefined
      const result = await api.productsStock(
        products.map(p => p.id),
        { warehouseIds: warehouses?.map(w => w.id) },
      )
      Object.values(result).forEach(resultItem => {
        if (
          resultItem.inStock > 0 &&
          !branchesWithStock.map(branch => branch[0]).includes(branchId)
        ) {
          branchesWithStock.push([branchId, resultItem.inStock])
        }
      })
    }

    const branches = await window.skeem.fetch2<IRecord<"branches", "name">[]>({
      model: {
        name: "branches",
        attributes: ["name"],
        filter: { matches: { id: branchesWithStock.map(branch => branch[0]) } },
      },
    })
    setLoadedStockBranches(
      branches.map(branch => ({
        ...branch,
        quantity: branchesWithStock.find(b => b[0] === branch.id)![1],
      })),
    )
  }

  return { handleLoadRelatedProductsStock, loadedStockBranches }
}

export function BoxQuantitySwitch({
  productId,
  itemQuantity,
  onChange,
}: {
  productId?: string
  itemQuantity?: number | null
  onChange: (bxQty: number | null) => void
}) {
  const [boxQuantity, setBoxQuantity] = useState<number | null>(null)

  function multipleOf(value: number, multipleOf: number) {
    return Math.ceil(value / multipleOf) * multipleOf
  }

  function handleUseBoxQty() {
    if (!boxQuantity) {
      return
    }
    const qty = itemQuantity ?? 0
    if (qty < boxQuantity) {
      onChange(boxQuantity)
    } else {
      onChange(multipleOf(qty, boxQuantity))
    }
  }

  const matchesBoxQty =
    boxQuantity !== null && multipleOf(itemQuantity ?? 0, boxQuantity) === itemQuantity

  useAsyncEffect(async () => {
    if (!productId) {
      return
    }
    const productWBox = await window.skeem.fetch2<{ box_quantity: number | null }>({
      first: {
        name: "products",
        attributes: [{ as: "box_quantity", value: { path: ["product_master", "box_quantity"] } }],
        filter: { matches: { id: productId } },
      },
    })
    if (productWBox?.box_quantity && productWBox.box_quantity > 0) {
      setBoxQuantity(productWBox.box_quantity)
    }
  }, [productId])

  if (boxQuantity === null || matchesBoxQty) {
    return null
  }
  return (
    <React.Fragment>
      <Spacer size={4} />
      <QuickToolTip
        content={`This product is packaged in boxes containing ${boxQuantity} units. Switch to this quantity for easier shipping and handling`}
      >
        <P size="small" blockSpacing={0}>
          Box qty: {boxQuantity}.{" "}
          <a
            onClick={handleUseBoxQty}
            css={css`
              cursor: pointer;
            `}
          >
            Switch? ({multipleOf(itemQuantity ?? 0, boxQuantity)})
          </a>
        </P>
      </QuickToolTip>
    </React.Fragment>
  )
}

function OrderRequestTextItemForm({
  item,
  error,
  onChange,
  onRemove,
}: {
  item: ITextItem
  error?: Record<string, string>
  onChange(item: ITextItem): void
  onRemove(): void
}) {
  return (
    <div
      css={css`
        padding: 2px 8px;
        margin: 0 -8px;
        transition: 0.2s;
      `}
    >
      <div>
        <Grid columns={[{ fr: 1 }, 36]} gap={8}>
          <Fields.TextArea
            value={item.body ?? ""}
            onChange={value => onChange({ ...item, body: value })}
            error={error?.body}
          />
          <IconButton
            onClick={onRemove}
            icon={icons.cross}
            buttonColor={brandColors.blaze}
            square
          />
        </Grid>
      </div>
    </div>
  )
}

export function OrderRequestAddressFormFields({
  type,
  disabled,
}: {
  type: "shipping_address" | "billing_address"
  disabled?: boolean
}) {
  const form = useForm()

  function handlePostcodeSelect(value: IPostcodeLookupReturnValue) {
    if (!form) {
      throw new Error("No address form")
    }

    form.setPartialData({
      [`${type}_city`]: value.city,
      [`${type}_country`]: value.country,
      [`${type}_county`]: value.county,
      [`${type}_line_1`]: value.line_1,
      [`${type}_line_2`]: value.line_2,
      [`${type}_line_3`]: value.line_3,
      [`${type}_name`]: value.name,
      [`${type}_postcode`]: value.postcode,
    })
  }

  Form2.useFormSubmitLock(() => {
    const postcodeField = form.getField(`${type}_postcode`)
    const countryField = form.getField(`${type}_country`)
    const value = postcodeField.getValue()
    if (!value && countryField.getValue() === "United Kingdom") {
      postcodeField.setError("must be present")
      return false
    } else {
      postcodeField.setError(undefined)
    }
  })

  return (
    <Grid gap={8}>
      <Form2.Text
        field={`${type}_name`}
        placeholder="Name"
        label={<FieldLabel label="Name" fieldName={`${type}_name`} required />}
        autoComplete={autoCompleteOff}
        disabled={disabled}
      />

      <Grid columns={{ repeat: { times: "auto-fit", size: { min: 160, max: { fr: 1 } } } }} gap={8}>
        <Form2.Text
          field={`${type}_line_1`}
          placeholder="Line 1"
          label={<FieldLabel label="Line 1" fieldName={`${type}_line_1`} required />}
          autoComplete={autoCompleteOff}
          disabled={disabled}
        />
        <Form2.Text
          field={`${type}_line_2`}
          placeholder="Line 2"
          label="Line 2"
          autoComplete={autoCompleteOff}
          disabled={disabled}
        />
      </Grid>
      <Grid columns={{ repeat: { times: "auto-fit", size: { min: 160, max: { fr: 1 } } } }} gap={8}>
        <Form2.Text
          field={`${type}_line_3`}
          placeholder="Line 3"
          label="Line 3"
          autoComplete={autoCompleteOff}
          disabled={disabled}
        />
        <Form2.Text
          field={`${type}_line_4`}
          placeholder="Line 4"
          label="Line 4"
          autoComplete={autoCompleteOff}
          disabled={disabled}
        />
      </Grid>
      <Form2.Text
        field={`${type}_city`}
        placeholder="City"
        label="City"
        autoComplete={autoCompleteOff}
        disabled={disabled}
      />
      <Grid columns={{ repeat: { times: "auto-fit", size: { min: 160, max: { fr: 1 } } } }} gap={8}>
        <Form2.Text
          field={`${type}_county`}
          placeholder="County"
          label="County"
          autoComplete={autoCompleteOff}
          disabled={disabled}
        />
        {disabled ? (
          <Form2.Text
            field={`${type}_postcode`}
            placeholder="Postcode"
            label={<FieldLabel label="Postcode" fieldName={`${type}_postcode`} required />}
            autoComplete={autoCompleteOff}
            disabled={disabled}
          />
        ) : (
          <Form2.WithField<string>
            field={`${type}_country`}
            subscribe={true}
            render={countryField => (
              <Form2.WithField
                field={`${type}_postcode`}
                render={field => (
                  <PostcodeField
                    label={
                      <Form2.FieldLabel
                        fieldName={`${type}_postcode`}
                        label="Postcode"
                        required={countryField.getValue() === "United Kingdom"}
                      />
                    }
                    onPostcodeSelect={handlePostcodeSelect}
                    postcodeFieldError={field.getError()}
                    field={`${type}_postcode`}
                  />
                )}
                subscribe
              />
            )}
          />
        )}
        <Form2ValidatePostcodeLength field="postcode" />
      </Grid>
      <CountrySelect field={`${type}_country`} />
      <Form2.Phone field={`${type}_phone`} placeholder="Phone" label="Phone" disabled={disabled} />
      <Form2.Email
        field={`${type}_email`}
        placeholder="Email"
        label="Email"
        autoComplete={autoCompleteOff}
        disabled={disabled}
      />
    </Grid>
  )
}

type IUPBPrice = IRecord<"universal_price_band_products", "price_pennies"> & {
  price_band_name: string
}
export interface ITargetBranchProduct extends IProduct {
  orderRequestAlternativeProduct?: IProduct & {
    price_band_products: IUPBPrice[]
  }
  price_band_products: IUPBPrice[]
}

export async function handleLoadOrderRequestProductData(opts: {
  productId: string
  sourceBranchId: string
  targetBranchId: string
}) {
  const branches = await window.skeem.fetch2<
    { settings: IBranchSettings; id: string; warehouses: { id: string }[] }[]
  >({
    model: {
      name: "branches",
      attributes: [
        "settings",
        "warehouses",
      ],
      filter: { matches: { id: [opts.sourceBranchId, opts.targetBranchId] } },
    },
  })

  const sourceBranch = branches.find(b => b.id === opts.sourceBranchId)
  const targetBranch = branches.find(b => b.id === opts.targetBranchId)

  assert(sourceBranch, "Source branch not found")
  assert(targetBranch, "Target branch not found")

  const sourceBranchWarehouses = getStockWarehouseIdsForBranch(sourceBranch)
  const targetBranchWarehouses = getStockWarehouseIdsForBranch(targetBranch)

  const allWarehouses = Array.from(new Set([...sourceBranchWarehouses, ...targetBranchWarehouses]))

  const targetProductAttributes = [
    "stock_code",
    "body",
    "preferred_supplier_list_price_pennies",
    "cost_price_pennies",
    "product_master",
    { name: "branch", attributes: branchAttrs },
    { name: "product_group", attributes: ["preventOrderRequest"] },
    {
      name: "product_locations",
      attributes: ["warehouse"],
      filter: { matches: { warehouse: allWarehouses } },
    },
  ]

  const product = await window.skeem.fetch2<
    IRecord<"products", "stock_code" | "body" | "product_master" | "product_locations">
  >({
    first: {
      name: "products",
      attributes: ["stock_code", "body", "product_master", "product_locations"],
      filter: { matches: { id: opts.productId } },
    },
  })
  const products = await window.skeem.fetch2<ITargetBranchProduct[]>({
    model: {
      name: "products",
      attributes: [
        ...targetProductAttributes,
        {
          name: "orderRequestAlternativeProduct",
          attributes: [
            ...targetProductAttributes,
            {
              as: "price_band_products",
              value: {
                path: [
                  "product_master",
                  {
                    name: "universal_price_band_products",
                    attributes: [
                      "price_pennies",
                      { as: "price_band_name", value: { path: ["universal_price_band", "name"] } },
                    ],
                    filter: {
                      gte: [
                        {
                          count: {
                            path: [
                              {
                                name: "universal_price_band",
                                filter: { matches: { name: { or: ["BRANCHES", "BRANCHES-EGS"] } } },
                              },
                            ],
                          },
                        },
                        { value: 1 },
                      ],
                    },
                  },
                ],
              },
            },
          ],
        },
        {
          as: "price_band_products",
          value: {
            path: [
              "product_master",
              {
                name: "universal_price_band_products",
                attributes: [
                  "price_pennies",
                  { as: "price_band_name", value: { path: ["universal_price_band", "name"] } },
                ],
                filter: {
                  gte: [
                    {
                      count: {
                        path: [
                          {
                            name: "universal_price_band",
                            filter: { matches: { name: { or: ["BRANCHES", "BRANCHES-EGS"] } } },
                          },
                        ],
                      },
                    },
                    { value: 1 },
                  ],
                },
              },
            ],
          },
        },
      ],
      filter: {
        eq: [{ lowercase: { attr: "stock_code" } }, { value: product.stock_code.toLowerCase() }],
      },
    },
  })

  // for each of the products, we want to strip out the non-relevant UPBs. if branch is EGS
  // - only use the BRANCHES-EGS ones, otherwise use the BRANCHES ones
  const upbName = opts.sourceBranchId === egsBranchId ? "BRANCHES-EGS" : "BRANCHES"
  products.forEach(product => {
    if (product.orderRequestAlternativeProduct) {
      product.orderRequestAlternativeProduct.price_band_products =
        product.orderRequestAlternativeProduct.price_band_products.filter(
          x => x.price_band_name === upbName,
        )
    }
    product.price_band_products = product.price_band_products.filter(
      x => x.price_band_name === upbName,
    )
  })

  const sourceBranchData = products.find(
    p => p.id === product.id && p.branch.id === opts.sourceBranchId,
  )

  const targetBranchData =
    sourceBranchData?.orderRequestAlternativeProduct ??
    products.find(p => p.branch.id === opts.targetBranchId)

  if (targetBranchData) {
    // strip out product locations not in target branch warehouses as we can now have the same source/target branch
    targetBranchData.product_locations = targetBranchData.product_locations.filter(
      pl => pl.warehouse && targetBranchWarehouses.includes(pl.warehouse.id),
    )
  }

  const productItem = sourceBranchData?.orderRequestAlternativeProduct
    ? {
        id: sourceBranchData.orderRequestAlternativeProduct.id,
        body: sourceBranchData.orderRequestAlternativeProduct.body,
        stock_code: sourceBranchData.orderRequestAlternativeProduct.stock_code,
        product_master: sourceBranchData.orderRequestAlternativeProduct.product_master,
      }
    : product

  const isInternal = await isInternalOrder(opts.sourceBranchId, opts.targetBranchId)

  let targetBranchPrice =
    targetBranchData?.cost_price_pennies || sourceBranchData?.cost_price_pennies

  const priceBandPrice =
    targetBranchData?.price_band_products?.[0] || sourceBranchData?.price_band_products?.[0]
  if (!isInternal && priceBandPrice?.price_pennies) {
    targetBranchPrice = priceBandPrice.price_pennies
    console.log("Found target branch price from branches price band", targetBranchPrice)
  }
  return {
    product: productItem,
    sourceBranchData,
    targetBranchData,
    alternative: !!sourceBranchData?.orderRequestAlternativeProduct,
    targetBranchPrice,
    isInternal,
  }
}

function LoadDataFromHistoryState() {
  const form = useForm()
  const itemsField = useField<IItem[]>("items", {
    subscribe: true,
    defaultValue: [],
  })
  const history = useHistory<{ productIds: string[] }>()

  useAsyncEffect(async () => {
    if (!history.location.state?.productIds) {
      return
    }
    const sourceBranch = form.getField("created_by_branch").getValue()
    const targetBranch = form.getField("received_by_branch").getValue()

    const productData = []

    for (const productId of history.location.state.productIds) {
      const { product, sourceBranchData, targetBranchData, alternative, targetBranchPrice } =
        await handleLoadOrderRequestProductData({
          sourceBranchId: sourceBranch.id,
          targetBranchId: targetBranch.id,
          productId: productId,
        })

      productData.push({
        id: `${ORDER_REQUEST_ITEM_NEW_ID_PREFIX}_${genId()}`,
        stock_code: product.stock_code,
        body: product.body,
        target_cost_price_pennies: targetBranchData?.cost_price_pennies,
        source_cost_price_pennies: sourceBranchData?.cost_price_pennies,
        target_product: targetBranchData,
        source_product: sourceBranchData,
        type: "product" as const,
        price_pennies: targetBranchPrice,
        alternative,
      })
    }

    itemsField.setValue(productData)
  }, [])

  return null
}

const COLUMNS = (cols?: { discountPct?: boolean }) =>
  [{ min: 200, max: "auto" }, 220, 80, cols?.discountPct && 100, 100, 100, 140, 160, 36].filter(
    Boolean,
  ) as any[]

function ConditionalAsyncRender<T>({
  fn,
  children,
  deps = [],
}: {
  fn: () => Promise<T>
  children: (result: T) => React.ReactNode
  deps: any[]
}) {
  const [result, setResult] = useState<any>(undefined)
  useAsyncEffect(async () => {
    const result = await fn()
    setResult(result)
  }, deps)
  return result !== undefined ? <React.Fragment>{children(result)}</React.Fragment> : null
}

type IBranchForSelect = IRecord<"branches"> & {
  orderRequestBranches: {
    targetBranch: IRecord<"branches", "name"> & { settings: IBranchSettings }
  }[]
}

export function OrderRequestBranchSelect({
  branchId,
  onChange,
  branchValue,
  disabled,
  label,
}: {
  label: string
  branchId: string
  branchValue?: string
  onChange: (branch: IRecord<"branches", "name"> & { settings: IBranchSettings }) => void
  disabled?: boolean
}) {
  const [isLoading, setIsLoading] = useState(true)
  const [branches, setBranches] = useState<IBranchForSelect["orderRequestBranches"] | null>(null)

  useAsyncEffect(async () => {
    const filter: any = {
      and: [],
    }
    filter.and.push(
      { not: { matches: { name: { like: "test" } } } },
      { not: { matches: { name: { like: "demo" } } } },
      { not: { matches: { name: { like: "training" } } } },
    )

    setIsLoading(true)
    try {
      const branch = await window.skeem.fetch2<IBranchForSelect>({
        first: {
          name: "branches",
          matches: { id: branchId },
          attributes: [
            {
              name: "orderRequestBranches",
              attributes: [
                {
                  name: "targetBranch",
                  attributes: ["name", "settings"],
                },
              ],
            },
          ],
        },
      })

      setBranches(branch.orderRequestBranches)
    } finally {
      setIsLoading(false)
    }
  }, [branchId])

  return (
    <Fields.Select2
      label={label}
      getOptionLabel={opt => opt.targetBranch.name}
      getOptionValue={opt => opt.targetBranch.id}
      options={branches ?? []}
      isLoading={isLoading}
      onChange={record => {
        onChange(record.targetBranch)
      }}
      value={branches?.find(b => b.targetBranch.id === branchValue)}
      disabled={disabled}
    />
  )
}

export function getStockWarehouseIdsForBranch(
  branch: IRecord<"branches", "warehouses"> & {
    settings?: IBranchSettings
  },
) {
  const stockWarehouseIds = branch.settings?.stockWarehouses.map(w => w.id) ?? []

  if (stockWarehouseIds.length > 0) {
    return stockWarehouseIds
  }

  const warehouseIds = branch.settings?.warehouses?.map(w => w.id) ?? []

  if (warehouseIds.length > 0) {
    return warehouseIds
  }

  return branch.warehouses?.map(w => w.id) ?? []
}
